import { faInfoCircle, faSave } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import MessageTemplateAutocomplete from 'components/common/MessageTemplateAutocomplete';
import MessageTemplateSelector from 'components/common/MessageTemplateSelector';
import Editor from 'components/editor/Editor';
import Loading from 'components/loading/Loading';
import useApi from 'helpers/api';
import {
  calculateEndTimeFromStartAndTimeSpent,
  calculateStartTimeFromEndAndTimeSpent,
  calculateTimeSpent
} from 'helpers/timeCalculations';
import { useAuthenticatedUser } from 'hooks/useAuthenticatedUser';
import { useEffect, useRef, useState } from 'react';
import {
  Button,
  Col,
  Form,
  Modal,
  OverlayTrigger,
  Row,
  Tooltip
} from 'react-bootstrap';
import MaskedInput from 'react-text-mask';
import PauseReasonsService from 'services/PauseReasons.service';

const TicketPauseRegistrationModal = ({
  ticketId,
  data,
  showModal,
  handleClose,
  handleSubmit,
  isLoading
}) => {
  const api = useApi();
  const { authenticatedUser } = useAuthenticatedUser();
  const [validated, setValidated] = useState(false);
  const [pauseReasons, setPauseReasons] = useState([]);
  const [agents, setAgents] = useState([]);
  const [filters] = useState({ includeInactives: false });

  const initialState = {
    description: '',
    typeService: 'INTERNAL',
    timeSpent: '',
    startTime: '',
    endTime: '',
    pauseSla: false,
    pauseReason: { id: '' },
    user: { id: authenticatedUser?.id || '' },
    backdateReason: '',
    slaEligible: false
  };
  const [body, setBody] = useState(initialState);
  const editorRef = useRef(null);
  const form = useRef(null);

  useEffect(() => {
    getPauseReasons();
    getAgents();
  }, []);

  useEffect(() => {
    if (showModal) {
      setValidated(false);
      // Pegar a hora atual quando o modal abrir
      const now = new Date();
      const currentTime = `${String(now.getHours()).padStart(2, '0')}:${String(
        now.getMinutes()
      ).padStart(2, '0')}`;

      // A hora INICIAL já vem preenchida com a hora que o técnico começou a trabalhar (baseado no timeSpent atual)
      // Se não houver data/hora anterior, usamos a atual como fallback
      let initialStartTime = '';
      if (data) {
        // Calcular hora início baseada na hora atual (fim) e tempo gasto já registrado
        // Isso é uma estimativa, pois na pausa "data" contém o tempo gasto até agora
        const calculatedStart = calculateStartTimeFromEndAndTimeSpent(
          currentTime,
          data
        );
        if (calculatedStart) {
          initialStartTime = calculatedStart;
        }
      }

      const initialBody = {
        ...initialState,
        timeSpent: data || '',
        endTime: currentTime,
        startTime: initialStartTime || currentTime, // Preenche com o cálculo ou hora atual
        user: { id: authenticatedUser?.id || '' }
      };

      setBody(initialBody);
    }
  }, [showModal, data, authenticatedUser]);

  const getPauseReasons = async () => {
    const queryParams = new URLSearchParams(filters).toString();
    const response = await PauseReasonsService.list(queryParams);

    response.content && setPauseReasons(response.content);
  };

  const getAgents = async () => {
    try {
      const queryParams = new URLSearchParams({
        page: 0,
        size: 1000,
        inactive: false
      }).toString();
      const response = await api.agents.list(queryParams);
      if (response.content) {
        setAgents(response.content);
      }
    } catch (error) {
      console.error('Erro ao buscar agentes:', error);
    }
  };

  const getTimeSpentStyle = () => {
    if (!(body.startTime && body.endTime)) return {};

    const isDark =
      document.documentElement.getAttribute('data-bs-theme') === 'dark';

    if (isDark) {
      return {
        backgroundColor: 'rgba(0, 210, 122, 0.2)',
        borderColor: 'rgba(0, 210, 122, 0.4)',
        color: '#00d27a'
      };
    }

    return {
      backgroundColor: '#d4edda',
      borderColor: '#c3e6cb'
    };
  };

  const isBackdated = () => {
    // Na pausa, só é retroativo se a hora de início for MENOR que a hora calculada originalmente
    // Ou seja, se o usuário alterar para um horário ANTERIOR ao que ele de fato começou
    if (!body.startTime || !data) return false;

    // Tolerância em minutos antes de considerar como retroativo
    const TOLERANCE_MINUTES = 10;

    const now = new Date();
    // Subtrair a tolerância da hora atual
    const adjustedTime = new Date(now.getTime() - TOLERANCE_MINUTES * 60000);
    const currentTime = `${String(adjustedTime.getHours()).padStart(
      2,
      '0'
    )}:${String(adjustedTime.getMinutes()).padStart(2, '0')}`;

    // Calcular quando ele deveria ter começado (hora atual - tolerância - tempo gasto)
    const expectedStart = calculateStartTimeFromEndAndTimeSpent(
      currentTime,
      data
    );

    // Se não conseguir calcular, assume falso
    if (!expectedStart) return false;

    // É retroativo se o início informado for menor que o esperado (com tolerância)
    return body.startTime < expectedStart;
  };

  const isDescriptionEmpty =
    body.description.trim() === '' || body.description === '<p><br></p>';

  const isBackdateReasonEmpty =
    isBackdated() &&
    (body.backdateReason.trim() === '' ||
      body.backdateReason === '<p><br></p>');

  const onSubmit = e => {
    if (
      form.current.checkValidity() === false ||
      body.timeSpent === '' ||
      body.startTime === '' ||
      body.endTime === '' ||
      isDescriptionEmpty ||
      isBackdateReasonEmpty
    ) {
      e.preventDefault();
      e.stopPropagation();
      setValidated(true);
    } else {
      handleSubmit({
        description: body.description,
        typeService: body.typeService,
        timeSpent: body.timeSpent,
        startTime: body.startTime,
        endTime: body.endTime,
        pauseSla: body.pauseSla,
        pauseReason: body.pauseReason,
        user: body.user,
        backdateReason: body.backdateReason,
        slaEligible: body.slaEligible
      });
      // Removido clearFields() - os campos só devem ser limpos quando o modal fechar após sucesso
    }
  };

  const clearFields = () => {
    setBody(initialState);
    setValidated(false);
  };

  const setFieldBody = (fieldName, value) => {
    setBody(prevBody => {
      const newBody = { ...prevBody };

      // Tratar campo user separadamente - sempre manter como objeto { id: number }
      if (fieldName === 'user') {
        newBody.user = { id: value };
      } else {
        newBody[fieldName] = value;
      }

      // Sincronização dos campos de tempo
      if (
        fieldName === 'startTime' ||
        fieldName === 'endTime' ||
        fieldName === 'timeSpent'
      ) {
        const start = fieldName === 'startTime' ? value : newBody.startTime;
        const end = fieldName === 'endTime' ? value : newBody.endTime;
        const timeSpent = fieldName === 'timeSpent' ? value : newBody.timeSpent;

        // Quando startTime + endTime = calcular timeSpent
        // (prioridade: se ambos estiverem preenchidos e não foi timeSpent que foi alterado)
        if (
          (fieldName === 'startTime' || fieldName === 'endTime') &&
          start &&
          end &&
          fieldName !== 'timeSpent'
        ) {
          const calculatedTimeSpent = calculateTimeSpent(start, end);
          if (calculatedTimeSpent) {
            newBody.timeSpent = calculatedTimeSpent;
            return newBody;
          }
        }

        // Quando startTime + timeSpent = calcular endTime
        // (só calcular se não foi endTime que foi alterado)
        if (
          (fieldName === 'startTime' || fieldName === 'timeSpent') &&
          start &&
          timeSpent &&
          fieldName !== 'endTime'
        ) {
          const calculatedEndTime = calculateEndTimeFromStartAndTimeSpent(
            start,
            timeSpent
          );
          if (calculatedEndTime) {
            newBody.endTime = calculatedEndTime;
            return newBody;
          }
        }

        // Quando endTime + timeSpent = calcular startTime
        // (só calcular se não foi startTime que foi alterado)
        if (
          (fieldName === 'endTime' || fieldName === 'timeSpent') &&
          end &&
          timeSpent &&
          fieldName !== 'startTime'
        ) {
          const calculatedStartTime = calculateStartTimeFromEndAndTimeSpent(
            end,
            timeSpent
          );
          if (calculatedStartTime) {
            newBody.startTime = calculatedStartTime;
            return newBody;
          }
        }
      }

      return newBody;
    });
  };

  return (
    <Modal size="lg" show={showModal} onHide={handleClose}>
      {isLoading && <Loading />}
      <Modal.Header closeButton>
        <Modal.Title>Pausar ticket</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form validated={validated} ref={form}>
          <Row className="mb-3">
            <Form.Group as={Col} md="6" controlId="appointment.type">
              <Form.Label>
                Tipo <span className="text-danger">*</span>
              </Form.Label>
              <Form.Select
                aria-label="Tipo"
                value={body.typeService}
                onChange={e => setFieldBody('typeService', e.target.value)}
                required
              >
                <option value="INTERNAL" key="INTERNAL">
                  Interno
                </option>
                <option value="EXTERNAL" key="EXTERNAL">
                  Externo
                </option>
              </Form.Select>
              <Form.Control.Feedback>Muito bom!</Form.Control.Feedback>
              <Form.Control.Feedback type="invalid">
                Escolha uma opção!
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group as={Col} md="6" controlId="appointment.agent">
              <Form.Label>
                Agente <span className="text-danger">*</span>
              </Form.Label>
              <Form.Select
                aria-label="Agente"
                value={body.user?.id || ''}
                onChange={e =>
                  setFieldBody(
                    'user',
                    e.target.value ? Number(e.target.value) : ''
                  )
                }
                required
              >
                {agents.map(agent => (
                  <option key={agent.id} value={agent.id}>
                    {agent.firstName} {agent.lastName}
                  </option>
                ))}
              </Form.Select>
              <Form.Control.Feedback>Muito bom!</Form.Control.Feedback>
              <Form.Control.Feedback type="invalid">
                Selecione um agente!
              </Form.Control.Feedback>
            </Form.Group>
          </Row>

          <Row className="mb-3">
            <Form.Group as={Col} md="4" controlId="appointment.startTime">
              <Form.Label>
                Hora Início <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="time"
                required
                value={body.startTime}
                onChange={e => setFieldBody('startTime', e.target.value)}
              />
              <Form.Control.Feedback>Muito bom!</Form.Control.Feedback>
              <Form.Control.Feedback type="invalid">
                Insira a hora de início!
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group as={Col} md="4" controlId="appointment.endTime">
              <Form.Label>
                Hora Fim <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="time"
                required
                value={body.endTime}
                onChange={e => setFieldBody('endTime', e.target.value)}
              />
              <Form.Control.Feedback>Muito bom!</Form.Control.Feedback>
              <Form.Control.Feedback type="invalid">
                Insira a hora de fim!
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group as={Col} md="4" controlId="appointment.timeSpent">
              <Form.Label>
                Tempo <span className="text-danger">*</span>
              </Form.Label>
              <MaskedInput
                mask={[/[0-9]/, /\d/, ':', /\d/, /\d/]}
                placeholder="HH:MM"
                required
                guide={false}
                value={body.timeSpent}
                onChange={e => setFieldBody('timeSpent', e.target.value)}
                style={getTimeSpentStyle ? getTimeSpentStyle() : {}}
                render={(ref, props) => <Form.Control ref={ref} {...props} />}
              />
              <Form.Control.Feedback>Muito bom!</Form.Control.Feedback>
              <Form.Control.Feedback type="invalid">
                Insira o tempo trabalhado!
              </Form.Control.Feedback>
            </Form.Group>
          </Row>

          <Row className="mb-3">
            <Form.Group as={Col} md={2}>
              <Form.Label>Pausar SLA</Form.Label>
              <Form.Switch
                value={body.pauseSla}
                onChange={() => setFieldBody('pauseSla', !body.pauseSla)}
                checked={body.pauseSla}
              />
            </Form.Group>

            {isBackdated() && (
              <Form.Group as={Col} md={3}>
                <Form.Label>
                  Regularizar SLA{' '}
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <Tooltip id="sla-tooltip">
                        Ao habilitar esta opção, se o tempo informado estiver
                        dentro do prazo de resposta, o SLA será marcado como
                        cumprido.
                      </Tooltip>
                    }
                  >
                    <span>
                      <FontAwesomeIcon
                        icon={faInfoCircle}
                        className="text-secondary cursor-pointer ms-1"
                      />
                    </span>
                  </OverlayTrigger>
                </Form.Label>
                <Form.Switch
                  value={body.slaEligible}
                  checked={body.slaEligible}
                  onChange={() =>
                    setFieldBody('slaEligible', !body.slaEligible)
                  }
                />
              </Form.Group>
            )}

            {body.pauseSla && (
              <Form.Group as={Col} md={4}>
                <Form.Label>
                  Motivo de pausa <span className="text-danger">*</span>
                </Form.Label>
                <Form.Select
                  required={body.pauseReason}
                  value={body.pauseReason.id}
                  onChange={e =>
                    setBody({ ...body, pauseReason: { id: e.target.value } })
                  }
                >
                  <option value="">Selecione</option>
                  {pauseReasons.map((item, i) => (
                    <option value={item.id} key={i}>
                      {item.name}
                    </option>
                  ))}
                </Form.Select>
                <Form.Control.Feedback>Muito bom!</Form.Control.Feedback>
                <Form.Control.Feedback type="invalid">
                  Motivo de pausa obrigatório.
                </Form.Control.Feedback>
              </Form.Group>
            )}
          </Row>

          <Form.Group controlId="appointment.profile">
            {isBackdated() && (
              <Form.Group
                className="mb-3"
                controlId="appointment.backdateReason"
              >
                <div
                  className="alert alert-warning py-2 mb-2 fs-10"
                  role="alert"
                >
                  <strong>Atenção:</strong> O horário do apontamento é
                  retroativo. Por favor, forneça uma justificativa.
                </div>
                <Form.Label>
                  Justificativa <span className="text-danger">*</span>
                </Form.Label>
                <textarea
                  className="form-control"
                  rows={2}
                  value={body.backdateReason}
                  onChange={e => setFieldBody('backdateReason', e.target.value)}
                  required={isBackdated()}
                />
                {validated && isBackdateReasonEmpty && (
                  <span className="d-block text-danger fs-10 mt-1">
                    Justificativa é obrigatória para apontamentos retroativos.
                  </span>
                )}
              </Form.Group>
            )}
            <Form.Label className="d-flex align-items-center justify-content-between">
              <span>
                Descrição
                <span className="text-danger"> *</span>
              </span>
              <MessageTemplateSelector
                type="APPOINTMENT"
                ticketId={ticketId}
                editorRef={editorRef}
                onTemplateApplied={content => {
                  setFieldBody('description', content);
                }}
              />
            </Form.Label>
            <Editor
              data={body.description}
              value={value => setFieldBody('description', value)}
              ref={editorRef}
            />
            <MessageTemplateAutocomplete
              editorRef={editorRef}
              type="APPOINTMENT"
              ticketId={ticketId}
              onTemplateApplied={content => {
                setFieldBody('description', content);
              }}
            />
            <Form.Control.Feedback>Muito bom!</Form.Control.Feedback>
            {validated && isDescriptionEmpty && (
              <span className="d-block text-danger fs-10 mt-1">
                Campo obrigatório.
              </span>
            )}
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="default" onClick={handleClose}>
          Fechar
        </Button>
        <Button variant="primary" type="submit" onClick={onSubmit}>
          <FontAwesomeIcon icon={faSave} className="me-2" />
          Salvar
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default TicketPauseRegistrationModal;
